// The bar takes the place of the add bar while a selection is running. "Move
// to" only lists the sections and works that can host lines, with their number.
import React, { useEffect, useRef, useState } from "react"
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome"
import Button from "@react/shared/ui/Button"

const SelectionBar = ({ count, targets, onDuplicate, onMove, onDelete, onClear }) => {
  const [moveOpen, setMoveOpen] = useState(false)
  const moveRef = useRef(null)

  useEffect(() => {
    if (!moveOpen) return undefined
    const onClick = event => { if (!moveRef.current?.contains(event.target)) setMoveOpen(false) }
    const onKey   = event => { if (event.key === "Escape") setMoveOpen(false) }
    document.addEventListener("click", onClick, true)
    document.addEventListener("keydown", onKey)
    return () => {
      document.removeEventListener("click", onClick, true)
      document.removeEventListener("keydown", onKey)
    }
  }, [moveOpen])

  return (
    <div className="p-3 shrink-0">
      <div className="flex flex-wrap items-center gap-2 rounded-xl border border-brand-200 bg-brand-50/60 p-2.5">
        <span className="pl-1 text-[12.5px] font-semibold text-brand-700 whitespace-nowrap">
          {I18n.t("quotes.builder.selection.count", { count })}
        </span>

        <span className="mx-0.5 h-5 w-px bg-brand-200" />

        <Button variant="secondary" size="sm" onClick={onDuplicate}>
          <FontAwesomeIcon icon="copy" />
          {I18n.t("quotes.builder.row_menu.duplicate")}
        </Button>

        <div className="relative" ref={moveRef}>
          <Button variant="secondary" size="sm" onClick={() => setMoveOpen(o => !o)} aria-expanded={moveOpen}>
            <FontAwesomeIcon icon="arrow-right-arrow-left" />
            {I18n.t("quotes.builder.selection.move_to")}
            <FontAwesomeIcon icon="chevron-down" className="!w-2.5 text-slate-400" />
          </Button>
          {moveOpen && (
            <div className="absolute bottom-full left-0 mb-1.5 max-h-[280px] min-w-[260px] overflow-y-auto bg-white border border-slate-200 rounded-[10px] p-1.5 shadow-lg z-[1200] font-sans text-[13px]">
              {targets.length === 0 ? (
                <p className="m-0 px-2.5 py-1.5 text-[12.5px] text-slate-400">
                  {I18n.t("quotes.builder.selection.no_target")}
                </p>
              ) : targets.map(target => (
                <button
                  key       = {target.uid}
                  type      = "button"
                  onClick   = {() => { setMoveOpen(false); onMove(target.uid) }}
                  className = "w-full flex items-center gap-2 px-2.5 py-1.5 rounded-md bg-transparent border-0 cursor-pointer text-left text-[13px] text-slate-700 hover:bg-slate-100 hover:text-slate-900"
                >
                  <span className="font-mono text-[11px] tabular-nums text-slate-400 shrink-0">{target.code}</span>
                  <span className="truncate">{target.label}</span>
                </button>
              ))}
            </div>
          )}
        </div>

        <Button variant="danger-ghost" size="sm" onClick={onDelete}>
          <FontAwesomeIcon icon="trash" />
          {I18n.t("quotes.builder.row_menu.delete")}
        </Button>

        <span className="flex-1" />

        <Button variant="ghost" size="sm" onClick={onClear}>
          {I18n.t("quotes.builder.selection.clear")}
        </Button>
      </div>
    </div>
  )
}

export default SelectionBar
